import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Flex,
  Image,
  Icon,
  List,
  ListItem,
} from "@chakra-ui/react";
import { FaCheckCircle } from "react-icons/fa";
import { NavbarWithCallToAction } from "../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getColor } from "../brandConfig";

const CapabilityPage = () => {
  return (
    <>
      <NavbarWithCallToAction />

      <Box
        bg={getColor("background.main")}
        minHeight="100vh"
        position="relative"
        overflow="hidden"
        _before={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(60% 60% at 0% 0%, #112334 0%, transparent 50%),
            radial-gradient(50% 50% at 50% 50%, #369eff33 0%, transparent 100%)
          `,
          pointerEvents: 'none',
          zIndex: 0,
        }}
        sx={{
          '@keyframes snowfall': {
            '0%': {
              transform: 'translateX(-200px) translateY(-100px)',
              opacity: 0,
            },
            '5%': {
              opacity: 0.4,
            },
            '25%': {
              transform: 'translateX(25vw) translateY(25vh)',
              opacity: 0.8,
            },
            '50%': {
              transform: 'translateX(50vw) translateY(50vh)',
              opacity: 1,
            },
            '75%': {
              transform: 'translateX(75vw) translateY(75vh)',
              opacity: 0.8,
            },
            '95%': {
              opacity: 0.4,
            },
            '100%': {
              transform: 'translateX(calc(100vw + 200px)) translateY(100vh)',
              opacity: 0,
            }
          },
          '@keyframes snowfall-slow': {
            '0%': {
              transform: 'translateX(-250px) translateY(-100px)',
              opacity: 0,
            },
            '5%': {
              opacity: 0.3,
            },
            '30%': {
              transform: 'translateX(35vw) translateY(30vh)',
              opacity: 0.6,
            },
            '60%': {
              transform: 'translateX(65vw) translateY(60vh)',
              opacity: 0.8,
            },
            '95%': {
              opacity: 0.3,
            },
            '100%': {
              transform: 'translateX(calc(100vw + 250px)) translateY(100vh)',
              opacity: 0,
            }
          },
          '@keyframes snowfall-medium': {
            '0%': {
              transform: 'translateX(-180px) translateY(-100px)',
              opacity: 0,
            },
            '8%': {
              opacity: 0.5,
            },
            '40%': {
              transform: 'translateX(45vw) translateY(40vh)',
              opacity: 0.9,
            },
            '70%': {
              transform: 'translateX(80vw) translateY(70vh)',
              opacity: 0.7,
            },
            '92%': {
              opacity: 0.4,
            },
            '100%': {
              transform: 'translateX(calc(100vw + 180px)) translateY(100vh)',
              opacity: 0,
            }
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '-10%',
            width: '120%',
            height: '100%',
            backgroundImage: `
              radial-gradient(3px 3px at 10% 10%, rgba(255,255,255,0.6) 50%, transparent 50%),
              radial-gradient(4px 4px at 20% 30%, rgba(255,255,255,0.5) 50%, transparent 50%),
              radial-gradient(2px 2px at 15% 50%, rgba(255,255,255,0.7) 50%, transparent 50%),
              radial-gradient(5px 5px at 25% 20%, rgba(255,255,255,0.4) 50%, transparent 50%),
              radial-gradient(3px 3px at 30% 60%, rgba(255,255,255,0.6) 50%, transparent 50%),
              radial-gradient(6px 6px at 35% 40%, rgba(255,255,255,0.3) 50%, transparent 50%),
              radial-gradient(4px 4px at 40% 70%, rgba(255,255,255,0.5) 50%, transparent 50%),
              radial-gradient(2px 2px at 45% 15%, rgba(255,255,255,0.8) 50%, transparent 50%),
              radial-gradient(5px 5px at 50% 80%, rgba(255,255,255,0.4) 50%, transparent 50%)
            `,
            backgroundSize: '400px 400px, 500px 500px, 350px 350px, 450px 450px, 550px 550px, 600px 600px, 480px 480px, 420px 420px, 520px 520px',
            backgroundPosition: '0 0, 80px 60px, 160px 40px, 240px 100px, 320px 160px, 400px 80px, 480px 200px, 560px 20px, 640px 180px',
            animation: 'snowfall 25s linear infinite',
            pointerEvents: 'none',
            zIndex: 1,
          }
        }}
      >
        {/* Snow effect layers */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          pointerEvents="none"
          zIndex={1}
        >
          <Box
            position="absolute"
            top={0}
            left="-20%"
            width="140%"
            height="100%"
            opacity={0.6}
            sx={{
              backgroundImage: `
                radial-gradient(5px 5px at 5% 15%, rgba(255,255,255,0.4) 50%, transparent 50%),
                radial-gradient(3px 3px at 15% 35%, rgba(255,255,255,0.5) 50%, transparent 50%),
                radial-gradient(7px 7px at 10% 55%, rgba(255,255,255,0.3) 50%, transparent 50%),
                radial-gradient(4px 4px at 20% 25%, rgba(255,255,255,0.6) 50%, transparent 50%),
                radial-gradient(8px 8px at 25% 45%, rgba(255,255,255,0.25) 50%, transparent 50%),
                radial-gradient(6px 6px at 30% 65%, rgba(255,255,255,0.45) 50%, transparent 50%),
                radial-gradient(3px 3px at 35% 20%, rgba(255,255,255,0.7) 50%, transparent 50%)
              `,
              backgroundSize: '600px 600px, 700px 700px, 550px 550px, 650px 650px, 750px 750px, 680px 680px, 580px 580px',
              backgroundPosition: '0 0, 120px 140px, 240px 80px, 360px 200px, 480px 280px, 600px 120px, 720px 40px',
              animation: 'snowfall-slow 35s linear infinite',
            }}
          />

          <Box
            position="absolute"
            top={0}
            left="-15%"
            width="130%"
            height="100%"
            opacity={0.5}
            sx={{
              backgroundImage: `
                radial-gradient(4px 4px at 8% 20%, rgba(255,255,255,0.5) 50%, transparent 50%),
                radial-gradient(6px 6px at 18% 40%, rgba(255,255,255,0.35) 50%, transparent 50%),
                radial-gradient(3px 3px at 12% 60%, rgba(255,255,255,0.6) 50%, transparent 50%),
                radial-gradient(5px 5px at 22% 30%, rgba(255,255,255,0.4) 50%, transparent 50%),
                radial-gradient(7px 7px at 28% 50%, rgba(255,255,255,0.3) 50%, transparent 50%),
                radial-gradient(4px 4px at 32% 70%, rgba(255,255,255,0.55) 50%, transparent 50%)
              `,
              backgroundSize: '550px 550px, 650px 650px, 500px 500px, 600px 600px, 700px 700px, 580px 580px',
              backgroundPosition: '50px 30px, 150px 150px, 250px 90px, 350px 210px, 450px 270px, 550px 150px',
              animation: 'snowfall-medium 30s linear infinite',
            }}
          />
        </Box>

        {/* Hero Section with Image Background - Full Width */}
        <Box
          position="relative"
          width="100vw"
          height="624px"
          overflow="hidden"
          marginLeft="50%"
          transform="translateX(-50%)"
          backgroundImage="url('/assets/onesolarimages/abouthero.jpg')"
          backgroundSize="cover"
          backgroundPosition="center"
          backgroundRepeat="no-repeat"
        >
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="rgba(0, 0, 0, 0.5)"
            zIndex={1}
          />

          <Container
            maxW="1200px"
            position="relative"
            zIndex={2}
            height="100%"
            display="flex"
            alignItems="center"
          >
            <Box
              width="100%"
              textAlign="center"
              color="white"
            >
              <Heading
                as="h1"
                fontSize={{ base: "4xl", md: "5xl", lg: "6xl" }}
                fontWeight="bold"
                textShadow="2px 2px 4px rgba(0,0,0,0.5)"
                fontFamily="'Open Sans', sans-serif"
                textTransform="uppercase"
              >
                Capability Statement
              </Heading>
              <Text
                fontSize={{ base: "xl", md: "2xl" }}
                mt={4}
                textShadow="2px 2px 4px rgba(0,0,0,0.5)"
                fontFamily="'Open Sans', sans-serif"
              >
                2024
              </Text>
            </Box>
          </Container>
        </Box>

        {/* Solar Smart Choice Section */}
        <Box
          bg="#121c47"
          py={{ base: 16, md: 20 }}
          position="relative"
        >
          <Container maxW="1200px">
            <VStack spacing={8} align="flex-start">
              <Box width="60px" height="2px" bg="white" />

              <Heading
                as="h2"
                fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
                fontWeight="bold"
                color="white"
                textTransform="uppercase"
                fontFamily="'Open Sans', sans-serif"
              >
                Solar for your home, it's a smart choice.
              </Heading>

              <Text
                fontSize={{ base: "lg", md: "xl" }}
                color="rgba(255, 255, 255, 0.9)"
                fontFamily="'Open Sans', sans-serif"
                maxW="800px"
              >
                We deliver the most efficient, affordable & reliable solar solutions to your home.
              </Text>
            </VStack>
          </Container>
        </Box>

        {/* About Us Section */}
        <Box
          bg="white"
          py={{ base: 16, md: 20 }}
          position="relative"
        >
          <Container maxW="1200px">
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={12} alignItems="center">
              <Box>
                <Image
                  src="/assets/onesolarimages/0002_solar-repairs-professional.jpg"
                  alt="Solar Technician"
                  width="100%"
                  height="auto"
                  borderRadius="lg"
                  objectFit="cover"
                />
              </Box>

              <VStack spacing={6} align="flex-start">
                <Box width="60px" height="2px" bg="#121c47" />

                <Heading
                  as="h3"
                  fontSize={{ base: "xs", md: "sm" }}
                  fontWeight="600"
                  textTransform="uppercase"
                  letterSpacing="wider"
                  color="#121c47"
                  fontFamily="'Open Sans', sans-serif"
                >
                  OUR HAMILTON SOLAR SPECIALISTS
                </Heading>

                <Heading
                  as="h2"
                  fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                  fontWeight="bold"
                  color="#050303"
                  textTransform="uppercase"
                  fontFamily="'Open Sans', sans-serif"
                >
                  One Solar, The Smart Choice.
                </Heading>

                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="#666"
                  fontFamily="'Open Sans', sans-serif"
                  lineHeight="1.8"
                >
                  Since our launch in 2020, One Solar New Zealand has been a leader in driving the switch to solar, helping homes and businesses choose greener energy. We take great pride in our commitment to excellence, innovation, and ensuring customer satisfaction.
                </Text>

                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="#666"
                  fontFamily="'Open Sans', sans-serif"
                  lineHeight="1.8"
                >
                  Whether it's setting up off-grid systems for those in remote areas or installing solar panels to help businesses cut costs, we bring expertise and experience to every project. We firmly believe that transitioning to solar energy is not just a choice, but a responsibility we all share.
                </Text>

                <Box
                  bg="#121c47"
                  color="white"
                  p={6}
                  borderRadius="md"
                  width="100%"
                >
                  <Text
                    fontSize={{ base: "md", md: "lg" }}
                    fontWeight="600"
                    fontFamily="'Open Sans', sans-serif"
                  >
                    With 10+ Years of Experience, One Solar is the Smart Choice in Solar.
                  </Text>
                </Box>
              </VStack>
            </SimpleGrid>
          </Container>
        </Box>

        {/* Team Expertise Section */}
        <Box
          bg="#f8f9fa"
          py={{ base: 16, md: 20 }}
          position="relative"
        >
          <Container maxW="1200px">
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={12}>
              <VStack spacing={6} align="flex-start">
                <Heading
                  as="h3"
                  fontSize={{ base: "xl", md: "2xl" }}
                  fontWeight="bold"
                  color="#050303"
                  fontFamily="'Open Sans', sans-serif"
                >
                  Expert Team
                </Heading>

                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="#666"
                  fontFamily="'Open Sans', sans-serif"
                  lineHeight="1.8"
                >
                  Our team is made up of experienced Energy Advisors and Certified installers who have dedicated themselves to understanding the ins and outs of solar energy. With a focus on putting our customers first, we take a personal approach to every project, making sure all interaction is informative, helpful, and satisfying.
                </Text>

                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="#666"
                  fontFamily="'Open Sans', sans-serif"
                  lineHeight="1.8"
                >
                  Choosing to partner with us means accessing top-notch expertise and personalized service to ensure your solar journey is smooth and successful. At One Solar New Zealand, our goal is to work hand in hand with you to deliver reliable, sustainable solar power solutions tailored to your needs.
                </Text>
              </VStack>

              <VStack spacing={6} align="flex-start">
                <Heading
                  as="h3"
                  fontSize={{ base: "xl", md: "2xl" }}
                  fontWeight="bold"
                  color="#050303"
                  fontFamily="'Open Sans', sans-serif"
                >
                  Quality Assurance (QA)
                </Heading>

                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="#666"
                  fontFamily="'Open Sans', sans-serif"
                  lineHeight="1.8"
                >
                  Quality is at the heart of everything we do. Our rigorous QA process involves thorough testing and certification of all our products and systems. Rest assured, we adhere strictly to industry standards and regulatory bodies, ensuring you receive nothing but the best.
                </Text>

                <Heading
                  as="h3"
                  fontSize={{ base: "xl", md: "2xl" }}
                  fontWeight="bold"
                  color="#050303"
                  fontFamily="'Open Sans', sans-serif"
                  mt={4}
                >
                  Solar Evaluation and Consultation (SEC)
                </Heading>

                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="#666"
                  fontFamily="'Open Sans', sans-serif"
                  lineHeight="1.8"
                >
                  We understand that every client is unique. That's why our Solar Evaluation and Consultation (SEC) phase begins with a comprehensive assessment of your energy requirements, site specifications, and project feasibility. We tailor our solutions to fit your needs perfectly, ensuring optimal performance and maximum savings.
                </Text>
              </VStack>
            </SimpleGrid>
          </Container>
        </Box>

        {/* Our Values Section */}
        <Box
          bg="white"
          py={{ base: 16, md: 20 }}
          position="relative"
        >
          <Container maxW="1200px">
            <VStack spacing={12} align="stretch">
              <Box>
                <Box width="60px" height="2px" bg="#121c47" mb={4} />
                <Heading
                  as="h2"
                  fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                  fontWeight="bold"
                  color="#050303"
                  textTransform="uppercase"
                  fontFamily="'Open Sans', sans-serif"
                  mb={6}
                >
                  Our Values
                </Heading>

                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="#666"
                  fontFamily="'Open Sans', sans-serif"
                  lineHeight="1.8"
                  maxW="900px"
                >
                  Everything we do at One Solar New Zealand is anchored by our strong values. We commit to delivering maximum value with our premium products, ensuring you experience all the benefits that come with choosing solar. We believe in honesty, integrity, and transparency in all our interactions. When you partner with us, you can trust that we'll always have your best interests at heart.
                </Text>
              </Box>
            </VStack>
          </Container>
        </Box>

        {/* Our Services Section */}
        <Box
          bg="#121c47"
          py={{ base: 16, md: 20 }}
          position="relative"
        >
          <Container maxW="1200px">
            <VStack spacing={12} align="stretch">
              <Box>
                <Box width="60px" height="2px" bg="white" mb={4} />
                <Heading
                  as="h2"
                  fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                  fontWeight="bold"
                  color="white"
                  textTransform="uppercase"
                  fontFamily="'Open Sans', sans-serif"
                  mb={6}
                >
                  Our Services
                </Heading>

                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="rgba(255, 255, 255, 0.9)"
                  fontFamily="'Open Sans', sans-serif"
                  lineHeight="1.8"
                  maxW="900px"
                  mb={8}
                >
                  One Solar New Zealand is committed to offering exceptional solar energy solutions, customized to correspond with your varied energy requirements. Our comprehensive services cater to residential, commercial, and rural settings, ensuring the benefits of solar power reach every corner.
                </Text>
              </Box>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                {/* Off-Grid Solar Systems */}
                <Box
                  bg="rgba(255, 255, 255, 0.1)"
                  p={6}
                  borderRadius="lg"
                  border="1px solid rgba(255, 255, 255, 0.2)"
                >
                  <Heading
                    as="h3"
                    fontSize={{ base: "lg", md: "xl" }}
                    fontWeight="bold"
                    color="white"
                    fontFamily="'Open Sans', sans-serif"
                    mb={4}
                  >
                    1. Off-Grid Solar Systems
                  </Heading>
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color="rgba(255, 255, 255, 0.85)"
                    fontFamily="'Open Sans', sans-serif"
                    lineHeight="1.7"
                  >
                    Our custom off-grid solar solutions are designed to accommodate diverse energy needs - from powering small structures to meeting the demands of large commercial properties. We tailor your system based on your energy needs, future plans, and the specific traits of your location.
                  </Text>
                </Box>

                {/* Residential Solar */}
                <Box
                  bg="rgba(255, 255, 255, 0.1)"
                  p={6}
                  borderRadius="lg"
                  border="1px solid rgba(255, 255, 255, 0.2)"
                >
                  <Heading
                    as="h3"
                    fontSize={{ base: "lg", md: "xl" }}
                    fontWeight="bold"
                    color="white"
                    fontFamily="'Open Sans', sans-serif"
                    mb={4}
                  >
                    2. Residential Solar
                  </Heading>
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color="rgba(255, 255, 255, 0.85)"
                    fontFamily="'Open Sans', sans-serif"
                    lineHeight="1.7"
                  >
                    For most households, our grid-connected solar systems are the preferred choice. These smart systems use sunlight as much as possible and only draw from the grid when needed. Plus, if you produce extra power, you can sell it back to the grid.
                  </Text>
                </Box>

                {/* Commercial Solar */}
                <Box
                  bg="rgba(255, 255, 255, 0.1)"
                  p={6}
                  borderRadius="lg"
                  border="1px solid rgba(255, 255, 255, 0.2)"
                >
                  <Heading
                    as="h3"
                    fontSize={{ base: "lg", md: "xl" }}
                    fontWeight="bold"
                    color="white"
                    fontFamily="'Open Sans', sans-serif"
                    mb={4}
                  >
                    3. Commercial Solar
                  </Heading>
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color="rgba(255, 255, 255, 0.85)"
                    fontFamily="'Open Sans', sans-serif"
                    lineHeight="1.7"
                  >
                    Commercial businesses can significantly lower their electricity bills by investing in 20-30kW solar systems. These systems are designed to use sunlight during peak daylight hours and simplify energy use, with a quick return on investment.
                  </Text>
                </Box>

                {/* Solar Maintenance */}
                <Box
                  bg="rgba(255, 255, 255, 0.1)"
                  p={6}
                  borderRadius="lg"
                  border="1px solid rgba(255, 255, 255, 0.2)"
                >
                  <Heading
                    as="h3"
                    fontSize={{ base: "lg", md: "xl" }}
                    fontWeight="bold"
                    color="white"
                    fontFamily="'Open Sans', sans-serif"
                    mb={4}
                  >
                    4. Solar Maintenance
                  </Heading>
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color="rgba(255, 255, 255, 0.85)"
                    fontFamily="'Open Sans', sans-serif"
                    lineHeight="1.7"
                  >
                    When it comes to keeping your solar power system in top shape, regular maintenance is key. At One Solar New Zealand, we offer comprehensive maintenance services to ensure your system operates at its best for years to come.
                  </Text>
                </Box>

                {/* Solar Monitoring */}
                <Box
                  bg="rgba(255, 255, 255, 0.1)"
                  p={6}
                  borderRadius="lg"
                  border="1px solid rgba(255, 255, 255, 0.2)"
                >
                  <Heading
                    as="h3"
                    fontSize={{ base: "lg", md: "xl" }}
                    fontWeight="bold"
                    color="white"
                    fontFamily="'Open Sans', sans-serif"
                    mb={4}
                  >
                    5. Solar Monitoring
                  </Heading>
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color="rgba(255, 255, 255, 0.85)"
                    fontFamily="'Open Sans', sans-serif"
                    lineHeight="1.7"
                  >
                    Solar Analytics makes it simple to align your energy use with solar production, letting you shift appliance use to sunny hours to save more. Real-time insights ensure you use solar energy efficiently.
                  </Text>
                </Box>

                {/* Solar Repairs */}
                <Box
                  bg="rgba(255, 255, 255, 0.1)"
                  p={6}
                  borderRadius="lg"
                  border="1px solid rgba(255, 255, 255, 0.2)"
                >
                  <Heading
                    as="h3"
                    fontSize={{ base: "lg", md: "xl" }}
                    fontWeight="bold"
                    color="white"
                    fontFamily="'Open Sans', sans-serif"
                    mb={4}
                  >
                    6. Solar Repairs
                  </Heading>
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color="rgba(255, 255, 255, 0.85)"
                    fontFamily="'Open Sans', sans-serif"
                    lineHeight="1.7"
                    mb={3}
                  >
                    We offer professional repair services to ensure your solar system operates efficiently. Our expert team swiftly diagnoses and fixes issues, restoring your system to its prime performance.
                  </Text>
                  <List spacing={1}>
                    <ListItem>
                      <HStack>
                        <Icon as={FaCheckCircle} color="white" boxSize={3} />
                        <Text fontSize="sm" color="rgba(255, 255, 255, 0.85)" fontFamily="'Open Sans', sans-serif">Smashed panels</Text>
                      </HStack>
                    </ListItem>
                    <ListItem>
                      <HStack>
                        <Icon as={FaCheckCircle} color="white" boxSize={3} />
                        <Text fontSize="sm" color="rgba(255, 255, 255, 0.85)" fontFamily="'Open Sans', sans-serif">Microcracks</Text>
                      </HStack>
                    </ListItem>
                    <ListItem>
                      <HStack>
                        <Icon as={FaCheckCircle} color="white" boxSize={3} />
                        <Text fontSize="sm" color="rgba(255, 255, 255, 0.85)" fontFamily="'Open Sans', sans-serif">Hotspots</Text>
                      </HStack>
                    </ListItem>
                    <ListItem>
                      <HStack>
                        <Icon as={FaCheckCircle} color="white" boxSize={3} />
                        <Text fontSize="sm" color="rgba(255, 255, 255, 0.85)" fontFamily="'Open Sans', sans-serif">Inverter problems</Text>
                      </HStack>
                    </ListItem>
                  </List>
                </Box>

                {/* Virtual Power Plants & Batteries */}
                <Box
                  bg="rgba(255, 255, 255, 0.1)"
                  p={6}
                  borderRadius="lg"
                  border="1px solid rgba(255, 255, 255, 0.2)"
                >
                  <Heading
                    as="h3"
                    fontSize={{ base: "lg", md: "xl" }}
                    fontWeight="bold"
                    color="white"
                    fontFamily="'Open Sans', sans-serif"
                    mb={4}
                  >
                    7. Virtual Power Plants, Batteries & Off-Grid Systems
                  </Heading>
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color="rgba(255, 255, 255, 0.85)"
                    fontFamily="'Open Sans', sans-serif"
                    lineHeight="1.7"
                  >
                    Want to maximize the benefits of your solar power system? Our Virtual Power Plants (VPPs) allow you to sell the energy you don't use at premium rates, providing you with additional savings while supporting a sustainable environment.
                  </Text>
                </Box>
              </SimpleGrid>
            </VStack>
          </Container>
        </Box>

        {/* Certified Technicians Section */}
        <Box
          bg="white"
          py={{ base: 16, md: 20 }}
          position="relative"
        >
          <Container maxW="1200px">
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={12} alignItems="center">
              <VStack spacing={6} align="flex-start">
                <Box width="60px" height="2px" bg="#121c47" />

                <Heading
                  as="h2"
                  fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                  fontWeight="bold"
                  color="#050303"
                  textTransform="uppercase"
                  fontFamily="'Open Sans', sans-serif"
                >
                  We are Hamilton's Most Trusted Solar Installer.
                </Heading>

                <Heading
                  as="h3"
                  fontSize={{ base: "xl", md: "2xl" }}
                  fontWeight="bold"
                  color="#050303"
                  fontFamily="'Open Sans', sans-serif"
                  mt={4}
                >
                  Certified Technicians
                </Heading>

                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="#666"
                  fontFamily="'Open Sans', sans-serif"
                  lineHeight="1.8"
                >
                  At One Solar New Zealand, we believe in delivering the highest quality in every aspect of our service. That's why our installations are carried out exclusively by certified technicians with industry-recognized qualifications.
                </Text>

                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="#666"
                  fontFamily="'Open Sans', sans-serif"
                  lineHeight="1.8"
                >
                  Our technicians are thoroughly trained to execute precise installations and follow industry standards. Rest assured that with our certified technicians, your solar system will be installed with top-notch performance and safety standards.
                </Text>
              </VStack>

              <Box>
                <Image
                  src="/assets/onesolarimages/services/residential_solar.jpg"
                  alt="Solar Installation"
                  width="100%"
                  height="auto"
                  borderRadius="lg"
                  objectFit="cover"
                />
              </Box>
            </SimpleGrid>
          </Container>
        </Box>

        {/* Smart Savings Section */}
        <Box
          bg="#f8f9fa"
          py={{ base: 16, md: 20 }}
          position="relative"
        >
          <Container maxW="1200px">
            <VStack spacing={8} align="stretch">
              <Box textAlign="center">
                <Heading
                  as="h2"
                  fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                  fontWeight="bold"
                  color="#050303"
                  textTransform="uppercase"
                  fontFamily="'Open Sans', sans-serif"
                  mb={6}
                >
                  Smart Savings with Solar
                </Heading>

                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="#666"
                  fontFamily="'Open Sans', sans-serif"
                  lineHeight="1.8"
                  maxW="900px"
                  mx="auto"
                >
                  Our high-quality solar panels are designed to harness sunlight and turn it into power for your house or business. This endless source of renewable energy can cover a large chunk of your power requirements, reducing your need for expensive grid-supplied electricity.
                </Text>

                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="#666"
                  fontFamily="'Open Sans', sans-serif"
                  lineHeight="1.8"
                  maxW="900px"
                  mx="auto"
                  mt={4}
                >
                  Based on your solar panel set-up and how much energy you normally use, you could look forward to slashing a significant part of your electric costs for at least 25 years, with many customers experiencing benefits for 30 to 40 years.
                </Text>
              </Box>
            </VStack>
          </Container>
        </Box>

        {/* Roof Protection Section */}
        <Box
          bg="white"
          py={{ base: 16, md: 20 }}
          position="relative"
        >
          <Container maxW="1200px">
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={12} alignItems="center">
              <Box>
                <Image
                  src="/assets/onesolarimages/services/residential_solar.jpg"
                  alt="Roof Protection"
                  width="100%"
                  height="auto"
                  borderRadius="lg"
                  objectFit="cover"
                />
              </Box>

              <VStack spacing={6} align="flex-start">
                <Box width="60px" height="2px" bg="#121c47" />

                <Heading
                  as="h2"
                  fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                  fontWeight="bold"
                  color="#050303"
                  textTransform="uppercase"
                  fontFamily="'Open Sans', sans-serif"
                >
                  Roof Protection
                </Heading>

                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="#666"
                  fontFamily="'Open Sans', sans-serif"
                  lineHeight="1.8"
                >
                  Our solar panels at One Solar New Zealand do more than just generate eco-friendly energy; they also provide a layer of protection that helps to extend the life of your roof. With careful and precise installation, our panels create a protective barrier that minimizes weather-related wear and tear.
                </Text>

                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="#666"
                  fontFamily="'Open Sans', sans-serif"
                  lineHeight="1.8"
                >
                  Installed using a cleverly-designed mounting system, our panels maintain a gap between them and the roof's surface. This crucial space prevents any direct contact or impact on your rooftop from the panels above.
                </Text>
              </VStack>
            </SimpleGrid>
          </Container>
        </Box>

        {/* Partnerships Section */}
        <Box
          bg="#121c47"
          py={{ base: 16, md: 20 }}
          position="relative"
        >
          <Container maxW="1200px">
            <VStack spacing={12} align="stretch">
              <Box textAlign="center">
                <Heading
                  as="h2"
                  fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                  fontWeight="bold"
                  color="white"
                  textTransform="uppercase"
                  fontFamily="'Open Sans', sans-serif"
                  mb={6}
                >
                  Our Partners Helping You in Your Solar Journey
                </Heading>
              </Box>

              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={8}>
                <Flex justify="center" align="center" bg="white" p={6} borderRadius="md" minH="120px">
                  <Image
                    src="/assets/onesolarimages/partners/Q-cells.png.webp"
                    alt="Q Cells"
                    maxH="80px"
                    objectFit="contain"
                  />
                </Flex>
                <Flex justify="center" align="center" bg="white" p={6} borderRadius="md" minH="120px">
                  <Image
                    src="/assets/onesolarimages/partners/fronius.png.webp"
                    alt="Fronius"
                    maxH="80px"
                    objectFit="contain"
                  />
                </Flex>
                <Flex justify="center" align="center" bg="white" p={6} borderRadius="md" minH="120px">
                  <Image
                    src="/assets/onesolarimages/partners/lg-chem.png.webp"
                    alt="LG Chem"
                    maxH="80px"
                    objectFit="contain"
                  />
                </Flex>
                <Flex justify="center" align="center" bg="white" p={6} borderRadius="md" minH="120px">
                  <Image
                    src="/assets/onesolarimages/partners/risen.png.webp"
                    alt="Risen"
                    maxH="80px"
                    objectFit="contain"
                  />
                </Flex>
                <Flex justify="center" align="center" bg="white" p={6} borderRadius="md" minH="120px">
                  <Image
                    src="/assets/onesolarimages/partners/solar-analytics.png.webp"
                    alt="Solar Analytics"
                    maxH="80px"
                    objectFit="contain"
                  />
                </Flex>
                <Flex justify="center" align="center" bg="white" p={6} borderRadius="md" minH="120px">
                  <Image
                    src="/assets/onesolarimages/partners/sungrow.jpg"
                    alt="Sungrow"
                    maxH="80px"
                    objectFit="contain"
                  />
                </Flex>
              </SimpleGrid>

              <Box mt={8}>
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="rgba(255, 255, 255, 0.9)"
                  fontFamily="'Open Sans', sans-serif"
                  lineHeight="1.8"
                  textAlign="center"
                >
                  We handpick our brand partners with a lot of care because your satisfaction and the quality of what we offer go hand in hand. We look for partners who are as excited about innovation and quality as we are, and who put customer service first. This makes sure every product you get from us is reliable and up to our high standards.
                </Text>
              </Box>
            </VStack>
          </Container>
        </Box>

        {/* Final CTA Section */}
        <Box
          bg="white"
          py={{ base: 16, md: 20 }}
          position="relative"
        >
          <Container maxW="1200px">
            <Box textAlign="center">
              <Heading
                as="h2"
                fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                fontWeight="bold"
                color="#050303"
                textTransform="uppercase"
                fontFamily="'Open Sans', sans-serif"
                mb={8}
              >
                We deliver the most efficient, affordable & reliable solar solutions to your home.
              </Heading>

              <Text
                fontSize={{ base: "md", md: "lg" }}
                color="#666"
                fontFamily="'Open Sans', sans-serif"
                maxW="800px"
                mx="auto"
              >
                Our team at One Solar New Zealand is ready to serve you with top-tier expertise and care.
              </Text>
            </Box>
          </Container>
        </Box>

        <FooterWithFourColumns />
      </Box>
    </>
  );
};

export default CapabilityPage;
